import * as S from './styled'
import { clubDisplayFilter } from '@app/courses/domain/searchFilters'
import React, { useState } from 'react'
import {
  getCountryFlagCode,
  getCountryFlagUrl,
} from '@app/courses/domain/countryFlags'
import ClubPlaceholder from '@app/courses/components/ClubPlaceholder'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import { Waypoint } from 'react-waypoint'
import images from '@app/images'
import { navigate } from '@app/navigation'
import theme from '@app/ui/theme'
import { useMediaQuery } from 'react-responsive'

import RefreshCourses from './RefreshCourses'
import Spacer from 'react-styled-spacer'
import { LRClub } from '../../../types/Clubs'
import ClubStatus from '../../../pages/ClubManagmentPage/ClubStatus'
import { MiProjectStatus } from '@app/evaluation-core/types'
import { Logo } from '@app/evaluation/components/Preparation.styles'

interface TableDataProps {
  clubs: LRClub[]
  searchValue: string
  filterStatusValue: number
  regionFilterStatusValue: string
  districtFilterStatusValue: string
  loading?: boolean
  onRefreshingChange?: (refreshing: boolean) => void
}

export const evalButtonTextForStatus = ['Start', 'Continue', 'Continue', 'View']

const Placeholders = () => (
  <>
    <ClubPlaceholder />
    <ClubPlaceholder />
    <ClubPlaceholder />
    <ClubPlaceholder />
    <ClubPlaceholder />
    <ClubPlaceholder />
  </>
)

const TableData = ({
  clubs,
  searchValue,
  filterStatusValue,
  regionFilterStatusValue,
  districtFilterStatusValue,
  loading,
  onRefreshingChange,
}: TableDataProps) => {
  const isPhone = window.innerWidth <= 767

  const [endIndex, setEndIndex] = useState(10)
  const isSmallScreen = useMediaQuery({ query: '(max-width: 960px)' })

  const onEndReached = () => setEndIndex(endIndex + 15)

  if (!clubs) return null

  if (loading) {
    return <Placeholders />
  }

  const results = clubs.filter((club, index) =>
    clubDisplayFilter(
      club,
      index,
      searchValue,
      filterStatusValue,
      regionFilterStatusValue,
      districtFilterStatusValue
    )
  )

  if (!results[0]) {
    return (
      <S.ClubsNoResults>
        <FeatherIcon name="frown" size={28} />{' '}
        <span>No clubs or courses matched your search critera.</span>
      </S.ClubsNoResults>
    )
  }

  const displayResults = results.slice(0, endIndex)

  return (
    <>
      <RefreshCourses onRefreshingChange={onRefreshingChange} />
      {displayResults.map((item, index) => {
        return (
          <React.Fragment key={index + item.id}>
            <S.ClubContainer>
              <S.ClubSideCol>
                <S.ClubLogo>
                  <Logo
                    src={item?.clubLogoUrl || images.png.CourseFallback}
                    onError={(e: any) => {
                      e.target.src = images.png.CourseFallback
                    }}
                  />
                </S.ClubLogo>
              </S.ClubSideCol>{' '}
              <S.ClubMainCol>
                <S.ClubHeader>
                  {isPhone ? (
                    <S.MobileClubTitle>{item.name}</S.MobileClubTitle>
                  ) : (
                    <S.ClubTitle>{item.name}</S.ClubTitle>
                  )}
                </S.ClubHeader>
                <ClubStatus
                  status={
                    item.projectStatus &&
                    (item.projectStatus.toString() as MiProjectStatus)
                  }
                  showLabel={true}
                  size={!isPhone ? 20 : 10}
                />

                <S.ClubSubHeader>
                  {!isPhone ? (
                    <div style={{ display: 'flex' }}>
                      <S.ClubSubHeaderCountryFlag>
                        <S.FlagImage
                          src={getCountryFlagUrl(
                            getCountryFlagCode(item.districtCountry)
                          )}
                        />
                      </S.ClubSubHeaderCountryFlag>
                      <Spacer w={15} />
                      <S.ClubSubHeaderSeparator />
                      <span>{item.districtName}</span>
                      <S.ClubSubHeaderSeparator />
                      <span>{item?.regionName || 'No region'}</span>
                    </div>
                  ) : (
                    <div
                      style={{
                        flexDirection: 'column',
                        width: '100%',
                      }}
                    >
                      <S.ClubSubHeaderCountryFlag>
                        <S.FlagImage
                          src={getCountryFlagUrl(
                            getCountryFlagCode(item.districtCountry)
                          )}
                        />
                      </S.ClubSubHeaderCountryFlag>
                      <Spacer w={15} />

                      <div
                        style={{
                          height: 20,
                          fontSize: 14,
                        }}
                      >
                        {item.districtName}
                      </div>

                      <div
                        style={{
                          height: 20,
                          fontSize: 14,
                        }}
                      >
                        {item?.regionName || 'No region'}
                      </div>
                      <RoundedButton
                        isDefault={true}
                        style={{
                          minWidth: isSmallScreen ? 115 : 250,
                          height: isSmallScreen ? 34 : 40,
                          marginLeft: 6,
                          marginRight: 6,
                        }}
                        onPress={() =>
                          navigate('ClubManagementStack', {
                            screen: 'ClubManagement',
                            params: { club: item },
                          })
                        }
                        title="Manage Club"
                      />
                      <Spacer h={5} />
                    </div>
                  )}
                  {!isPhone && (
                    <RoundedButton
                      isDefault={true}
                      style={{
                        minWidth: isSmallScreen ? 115 : 250,
                        height: isSmallScreen ? 34 : 40,
                      }}
                      onPress={() =>
                        navigate('ClubManagementStack', {
                          screen: 'ClubManagement',
                          params: { club: item },
                        })
                      }
                      title="Manage Club"
                    />
                  )}
                </S.ClubSubHeader>
              </S.ClubMainCol>
            </S.ClubContainer>
            {index + 1 !== displayResults.length && (
              <div
                style={{
                  width: 'calc(100% + 60px)',
                  marginLeft: -30,
                  border: `1px dotted ${theme.colors.primary}`,
                  opacity: 0.3,
                }}
              />
            )}
          </React.Fragment>
        )
      })}
      <Waypoint onEnter={onEndReached}>
        {endIndex < results.length ? (
          <div
            style={{
              width: '100%',
              height: '500px',
            }}
          >
            <ClubPlaceholder />
            <ClubPlaceholder />
            <ClubPlaceholder />
            <ClubPlaceholder />
            <ClubPlaceholder />
            <ClubPlaceholder />
          </div>
        ) : null}
      </Waypoint>
    </>
  )
}

export default TableData
